import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { Modal, Button } from 'antd';
import PageShell from '../components/page/PageShell';
import PageHeader from '../components/page/PageHeader';
import PageFilter from '../components/page/PageFilter';
import PageState from '../components/page/PageState';
import { useApp } from '../context/AppContext';
import { Role, View } from '../constants/roles';
import { normalizeMeetingAttendees } from '../utils/gapMeetingScheduleShared';
import { formatWeekdayDdMmYyyy, istMonthDay } from "../utils/formatDdMmYyyy";
import { displayEmail } from '../utils/displayEmail';

const TYPE_LABELS = {
    in_person: 'In-person Assessment',
    remote_video: 'Virtual (Teams/Zoom)',
    hybrid: 'Hybrid Assessment',
};

const STATUS_STYLES = {
    scheduled: 'bg-[color:var(--color-accent-tint)] text-[color:var(--color-accent)] border-[color:var(--color-info-line)]',
    rescheduled: 'bg-[color:var(--color-warn-bg)] text-[color:var(--color-warn-fg)] border-[color:var(--color-warn-line)]',
    awaiting_delegates: 'bg-[color:var(--color-accent-tint)] text-[color:var(--color-accent)] border-[color:var(--color-info-line)]',
    confirmed: 'bg-[color:var(--color-ok-bg)] text-[color:var(--color-ok-fg)] border-[color:var(--color-ok-line)]',
    completed: 'bg-[color:var(--color-bg-alt)] text-[color:var(--color-text-muted)] border-[color:var(--color-border)]',
    cancelled: 'bg-[color:var(--color-danger-bg)] text-[color:var(--color-danger-fg)] border-[color:var(--color-danger-line)]',
};

const STATUS_LABELS = {
    scheduled: 'Scheduled',
    rescheduled: 'Rescheduled',
    awaiting_delegates: 'Awaiting SPOC',
    confirmed: 'Confirmed',
    completed: 'Completed',
    cancelled: 'Cancelled',
};

function badgeForMeeting(meeting, isPast, effectiveStatus) {
    if (isPast) return { key: 'completed', label: 'Past' };
    const s = effectiveStatus || meeting.status || 'scheduled';
    return { key: s, label: STATUS_LABELS[s] || s };
}

const MeetingsPage = () => {
    const { fetchMeetings, fetchProjectMembers, updateMeetingAttendees, user, currentView } = useApp();
    const [meetings, setMeetings] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState('upcoming');

    const [delegateModalOpen, setDelegateModalOpen] = useState(false);
    const [delegateMeeting, setDelegateMeeting] = useState(null);
    const [projectMembers, setProjectMembers] = useState([]);
    const [selectedMemberIds, setSelectedMemberIds] = useState(() => new Set());
    const [savingDelegates, setSavingDelegates] = useState(false);

    const loadMeetings = useCallback(async () => {
        const data = await fetchMeetings();
        setMeetings(data);
    }, [fetchMeetings]);

    useEffect(() => {
        if (currentView !== View.MEETINGS) return;
        let cancelled = false;
        (async () => {
            setLoading(true);
            await loadMeetings();
            if (!cancelled) setLoading(false);
        })();
        return () => { cancelled = true; };
    }, [currentView, loadMeetings]);

    useEffect(() => {
        if (currentView !== View.MEETINGS) return;
        const onFocus = () => {
            void loadMeetings();
        };
        const onVis = () => {
            if (document.visibilityState === 'visible') void loadMeetings();
        };
        window.addEventListener('focus', onFocus);
        document.addEventListener('visibilitychange', onVis);
        return () => {
            window.removeEventListener('focus', onFocus);
            document.removeEventListener('visibilitychange', onVis);
        };
    }, [currentView, loadMeetings]);

    const now = new Date();

    const filtered = useMemo(() => {
        if (filter === 'upcoming') return meetings.filter((m) => new Date(m.meetingDate) >= now && m.status !== 'cancelled');
        if (filter === 'past') return meetings.filter((m) => new Date(m.meetingDate) < now || m.status === 'completed');
        return meetings;
    }, [meetings, filter, now]);

    const counts = useMemo(() => ({
        upcoming: meetings.filter((m) => new Date(m.meetingDate) >= now && m.status !== 'cancelled').length,
        past: meetings.filter((m) => new Date(m.meetingDate) < now || m.status === 'completed').length,
        total: meetings.length,
    }), [meetings, now]);

    const formatDate = (iso) => {
        return formatWeekdayDdMmYyyy(iso);
    };

    const isSpoc = user?.role === Role.COMPANY_SPOC;

    const openDelegateModal = async (meeting) => {
        setDelegateMeeting(meeting);
        setSelectedMemberIds(new Set());
        setDelegateModalOpen(true);
        const members = await fetchProjectMembers(meeting.projectId);
        setProjectMembers(Array.isArray(members) ? members : []);
    };

    const toggleMember = (id) => {
        setSelectedMemberIds((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    };

    const confirmDelegates = async () => {
        if (!delegateMeeting || selectedMemberIds.size === 0) return;
        setSavingDelegates(true);
        const attendees = projectMembers
            .filter((m) => selectedMemberIds.has(m.id))
            .map((m) => ({
                userId: m.id,
                name: m.name || m.email,
                email: m.email,
                role: m.role,
            }));
        const result = await updateMeetingAttendees(delegateMeeting.id, attendees);
        setSavingDelegates(false);
        if (result) {
            setDelegateModalOpen(false);
            setDelegateMeeting(null);
            setProjectMembers([]);
            setSelectedMemberIds(new Set());
            await loadMeetings();
        }
    };

    return (
        <PageShell>
            <PageHeader
                title="Meetings"
                subtitle="All scheduled meetings across your projects"
                actions={(
                    <div className="flex items-center gap-4 text-center">
                        <div className="bg-[color:var(--color-accent-tint)] rounded-2xl px-5 py-3 border border-[color:var(--color-info-line)]">
                            <span className="text-[10px] font-black uppercase tracking-widest text-[color:var(--color-accent)] block">Upcoming</span>
                            <span className="text-2xl font-black text-[color:var(--color-text-hero)]">{counts.upcoming}</span>
                        </div>
                        <div className="bg-[color:var(--color-surface-sunken)] rounded-2xl px-5 py-3 border border-[color:var(--color-border)]">
                            <span className="text-[10px] font-black uppercase tracking-widest text-[color:var(--color-text-muted)] block">Total</span>
                            <span className="text-2xl font-black text-[color:var(--color-text-hero)]">{counts.total}</span>
                        </div>
                    </div>
                )}
            />

            <PageFilter
                value={filter}
                onChange={setFilter}
                options={[
                    { value: 'upcoming', label: 'Upcoming' },
                    { value: 'past', label: 'Past' },
                    { value: 'all', label: 'All' },
                ]}
            />

            {loading ? (
                <PageState status="loading" message="Loading meetings..." />
            ) : filtered.length === 0 ? (
                <PageState
                    status="empty"
                    message={filter === 'upcoming' ? 'No upcoming meetings scheduled.' : 'No meetings match this filter.'}
                />
            ) : (
                <div className="space-y-3">
                    {filtered.map((meeting) => {
                        const isPast = new Date(meeting.meetingDate) < now;
                        const attendees = normalizeMeetingAttendees(meeting.attendees);
                        const effectiveStatus =
                            meeting.status === 'awaiting_delegates' && attendees.length > 0
                                ? 'scheduled'
                                : (meeting.status || 'scheduled');
                        const { key: statusStyleKey, label: statusLabel } = badgeForMeeting(
                            meeting,
                            isPast,
                            effectiveStatus,
                        );
                        const styleKey = STATUS_STYLES[statusStyleKey] ? statusStyleKey : 'scheduled';
                        const needsDelegates =
                            effectiveStatus === 'awaiting_delegates' && attendees.length === 0;
                        const showAddDelegates = isSpoc && needsDelegates && !isPast;

                        return (
                            <div key={meeting.id} className={`bg-[color:var(--color-bg-white)] rounded-2xl border shadow-sm overflow-hidden transition-all hover:shadow-md ${isPast ? 'border-[color:var(--color-border)] opacity-75' : 'border-[color:var(--color-border)]'}`}>
                                <div className="px-6 py-5 flex items-start gap-5">
                                    <div className={`w-14 h-14 rounded-2xl flex flex-col items-center justify-center shrink-0 ${isPast ? 'bg-[color:var(--color-bg-alt)]' : 'bg-[color:var(--color-accent-tint)] border border-[color:var(--color-info-line)]'}`}>
                                        <span className={`text-[10px] font-black uppercase ${isPast ? 'text-[color:var(--color-text-muted)]' : 'text-[color:var(--color-accent)]'}`}>
                                            {istMonthDay(meeting.meetingDate).month}
                                        </span>
                                        <span className={`text-lg font-black ${isPast ? 'text-[color:var(--color-text-muted)]' : 'text-[color:var(--color-text-hero)]'}`}>
                                            {istMonthDay(meeting.meetingDate).day}
                                        </span>
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2 mb-1 flex-wrap">
                                            <h3 className="text-sm font-bold text-[color:var(--color-text-hero)] truncate">{meeting.title}</h3>
                                            <span className={`px-2 py-0.5 rounded text-[8px] font-black uppercase border ${STATUS_STYLES[styleKey] || STATUS_STYLES.scheduled}`}>
                                                {statusLabel}
                                            </span>
                                        </div>
                                        <p className="text-[10px] text-[color:var(--color-text-muted)] font-medium mb-2">
                                            {formatDate(meeting.meetingDate)} &bull; {TYPE_LABELS[meeting.meetingType] || meeting.meetingType} &bull; {meeting.projectName}
                                        </p>
                                        {meeting.department && (
                                            <p className="text-[10px] text-[color:var(--color-text-muted)] font-semibold mb-2">
                                                Department: {meeting.department}
                                            </p>
                                        )}
                                        {attendees.length > 0 && (
                                            <div className="flex items-center gap-1.5 flex-wrap">
                                                <span className="text-[9px] font-bold text-[color:var(--color-text-muted)] uppercase mr-1">Attendees:</span>
                                                {attendees.map((a, i) => (
                                                    <span key={a.userId || i} className="inline-flex items-center gap-1 px-2 py-0.5 bg-[color:var(--color-surface-sunken)] border border-[color:var(--color-border)] rounded text-[9px] font-medium text-[color:var(--color-text-muted)]">
                                                        <span className="w-3.5 h-3.5 bg-[color:var(--color-accent-fill)] rounded text-white flex items-center justify-center text-[7px] font-black">
                                                            {(a.name || '?').charAt(0).toUpperCase()}
                                                        </span>
                                                        {a.name}
                                                    </span>
                                                ))}
                                            </div>
                                        )}
                                        {needsDelegates && (
                                            <p className="text-[10px] text-[color:var(--color-accent)] font-medium mt-1">Company SPOC will add delegates to this meeting.</p>
                                        )}
                                        {(meeting.meetingLocation || meeting.meetingLink) && (
                                            <div className="flex items-center gap-3 mt-2 flex-wrap">
                                                {meeting.meetingLocation && (
                                                    <span className="inline-flex items-center gap-1 text-[10px] text-[color:var(--color-text-muted)] font-medium">
                                                        <span>📍</span> {meeting.meetingLocation}
                                                    </span>
                                                )}
                                                {meeting.meetingLink && (
                                                    <a href={meeting.meetingLink} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-[10px] text-[color:var(--color-accent)] hover:text-[color:var(--color-accent)] hover:underline font-semibold">
                                                        <span>🔗</span> Join Meeting
                                                    </a>
                                                )}
                                            </div>
                                        )}
                                        {meeting.notes && (
                                            <p className="text-[10px] text-[color:var(--color-text-muted)] mt-2 line-clamp-2">{meeting.notes}</p>
                                        )}
                                        {showAddDelegates && (
                                            <div className="mt-3">
                                                <button
                                                    type="button"
                                                    onClick={() => openDelegateModal(meeting)}
                                                    className="px-4 py-2 rounded-xl text-[10px] font-black uppercase tracking-widest bg-[color:var(--color-accent-fill)] text-white hover:brightness-110 transition-all shadow-sm"
                                                >
                                                    Add delegates
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            <Modal
                title={delegateMeeting ? `Add delegates - ${delegateMeeting.title}` : 'Add delegates'}
                open={delegateModalOpen}
                onCancel={() => { setDelegateModalOpen(false); setDelegateMeeting(null); setProjectMembers([]); }}
                footer={[
                    <Button key="cancel" onClick={() => { setDelegateModalOpen(false); setDelegateMeeting(null); setProjectMembers([]); }}>
                        Cancel
                    </Button>,
                    <Button
                        key="ok"
                        type="primary"
                        loading={savingDelegates}
                        disabled={selectedMemberIds.size === 0}
                        onClick={confirmDelegates}
                        className="bg-[color:var(--color-accent-fill)]"
                    >
                        Confirm & notify attendees
                    </Button>,
                ]}
            >
                <p className="text-xs text-[color:var(--color-text-muted)] mb-3">Select project members to invite. They will receive an in-app notification.</p>
                <div className="max-h-[320px] overflow-y-auto space-y-1 border border-[color:var(--color-border)] rounded-lg p-2">
                    {projectMembers.map((m) => {
                        const selected = selectedMemberIds.has(m.id);
                        return (
                            <button
                                type="button"
                                key={m.id}
                                onClick={() => toggleMember(m.id)}
                                className={`w-full text-left flex items-center gap-3 px-3 py-2 rounded-lg border transition-all ${selected ? 'bg-[color:var(--color-accent-tint)] border-[color:var(--color-info-line)]' : 'bg-[color:var(--color-bg-white)] border-[color:var(--color-border)] hover:bg-[color:var(--color-bg-alt)]'}`}
                            >
                                <span className={`w-5 h-5 rounded flex items-center justify-center text-[10px] font-bold shrink-0 ${selected ? 'bg-[color:var(--color-accent-fill)] text-white' : 'bg-[color:var(--color-bg-alt)] text-[color:var(--color-text-muted)]'}`}>
                                    {selected ? '✓' : ''}
                                </span>
                                <div className="min-w-0 flex-1">
                                    <div className="text-xs font-bold text-[color:var(--color-text-hero)] truncate">{m.name || displayEmail(m.email)}</div>
                                    <div className="text-[10px] text-[color:var(--color-text-muted)] truncate">{displayEmail(m.email)}</div>
                                </div>
                                <span className="text-[8px] font-bold text-[color:var(--color-text-muted)] uppercase shrink-0">{(m.role || '').replace(/_/g, ' ')}</span>
                            </button>
                        );
                    })}
                </div>
                {projectMembers.length === 0 && delegateModalOpen && (
                    <p className="text-[10px] text-[color:var(--color-text-muted)] mt-2">No members loaded.</p>
                )}
            </Modal>
        </PageShell>
    );
};

export default MeetingsPage;
